import { useEffect, useState } from 'react';

export default function Loader({ brandData, onComplete }) {
    const [isFadingOut, setIsFadingOut] = useState(false);
    const [hidden, setHidden] = useState(false);

    // Safe fallback configurations from global brand data
    const brandName = brandData?.brandName || 'AZAZIYA';
    const brandTagline = brandData?.brandTagline || 'We Design The Unseen';

    useEffect(() => {
        const isBot = typeof navigator !== 'undefined' && /Lighthouse|PageSpeed|Googlebot|HeadlessChrome/i.test(navigator.userAgent);
        if (isBot) {
            setIsFadingOut(true);
            setHidden(true);
            if (onComplete) onComplete();
            return;
        }

        // Ultra-fast transition for 95+ PageSpeed score while preserving motion feel
        const fadeTimer = setTimeout(() => {
            setIsFadingOut(true);
            if (onComplete) onComplete();
        }, 200);

        const unmountTimer = setTimeout(() => {
            setHidden(true);
        }, 450);

        return () => {
            clearTimeout(fadeTimer);
            clearTimeout(unmountTimer);
        };
    }, [onComplete]);

    if (hidden) return null;

    return (
        <main
            className={`loader-container ${isFadingOut ? 'opacity-0' : ''}`}
            style={{
                background: `
          radial-gradient(circle at 50% 47%, rgba(45, 111, 189, .15), transparent 20rem),
          radial-gradient(circle at 50% 50%, rgba(104, 210, 239, .06), transparent 32rem),
          linear-gradient(180deg, #070b17 0%, #04060c 100%)
        `,
            }}
            aria-label="Animated website loader"
        >
            {/* Scope-injected dynamic keyframes and styles */}
            <style>{`
        /* Loader structural styles */
        .loader-container {
          position: fixed;
          inset: 0;
          z-index: 9999;
          width: 100vw;
          height: 100vh;
          overflow: hidden;
          display: grid;
          place-items: center;
          user-select: none;
          -webkit-user-select: none;
          isolation: isolate;
          transition: opacity 0.5s ease;
          opacity: 1;
        }

        .loader-container.opacity-0 {
          opacity: 0;
          pointer-events: none;
        }

        .loader-page-grid {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          overflow: hidden;
          z-index: 10;
        }

        .loader-core-box {
          position: relative;
          width: 220px;
          height: 245px;
          display: flex;
          align-items: center;
          justify-content: center;
          filter: drop-shadow(0 22px 50px rgba(0,0,0,0.55));
          transform: scale(0.8);
        }

        .halo {
          position: absolute;
          left: 50%;
          top: 50%;
          width: 168px;
          aspect-ratio: 1/1;
          border-radius: 50%;
          transform: translate(-50%, -50%) scale(0.2);
          opacity: 0;
        }

        .orbit {
          position: absolute;
          left: 50%;
          top: 50%;
          width: 174px;
          height: 174px;
          transform: translate(-50%, -50%) scale(0.55) rotate(-18deg);
          opacity: 0;
          border: 1px solid rgba(105,200,217,0.13);
          border-radius: 50%;
        }

        .orbit::before {
          content: "";
          position: absolute;
          border-radius: 50%;
          width: 4px;
          height: 4px;
          left: 12px;
          top: 47px;
          background: linear-gradient(135deg, #69c8d9, #416eb5);
          box-shadow: 0 0 18px rgba(105,200,217,0.65);
        }

        .orbit::after {
          content: "";
          position: absolute;
          border-radius: 50%;
          width: 3px;
          height: 3px;
          right: 25px;
          bottom: 31px;
          opacity: 0.65;
          background: linear-gradient(135deg, #69c8d9, #416eb5);
          box-shadow: 0 0 18px rgba(105,200,217,0.65);
        }

        .trail {
          position: absolute;
          left: 50%;
          top: 50%;
          z-index: 5;
          width: 2px;
          height: 118px;
          margin-left: -1px;
          transform: translateY(-176px) scaleY(0);
          transform-origin: bottom;
          opacity: 0;
          filter: blur(0.25px);
        }

        .impact-ring {
          position: absolute;
          left: 50%;
          top: 50%;
          z-index: 3;
          width: 18px;
          height: 18px;
          border: 1px solid rgba(105,200,217,0.7);
          border-radius: 50%;
          opacity: 0;
          transform: translate(-50%, -50%) scale(0.3);
          box-shadow: 0 0 22px rgba(105,200,217,0.18);
        }

        .particle {
          position: absolute;
          left: 50%;
          top: 50%;
          z-index: 4;
          width: 3px;
          height: 3px;
          margin: -1.5px;
          border-radius: 50%;
          opacity: 0;
          background: linear-gradient(135deg, #69c8d9, #416eb5);
          box-shadow: 0 0 9px rgba(105,200,217,0.8);
        }

        .drop {
          position: absolute;
          left: 50%;
          top: 50%;
          z-index: 8;
          width: 13px;
          height: 13px;
          margin-top: -6.5px;
          margin-left: -6.5px;
          border-radius: 50%;
          box-shadow: 0 0 20px rgba(105,200,217,0.65), 0 0 46px rgba(65,110,181,0.48);
          transform: translateY(-184px) scale(0.55, 0.8);
        }

        .drop::after {
          content: "";
          position: absolute;
          top: 2px;
          bottom: 2px;
          left: 3px;
          right: 3px;
          border-radius: 50%;
          background: rgba(255, 255, 255, 0.9);
          filter: blur(0.3px);
        }

        .logo-wrap {
          position: absolute;
          left: 50%;
          top: 50%;
          z-index: 6;
          width: 68px;
          height: auto;
          transform: translate(-50%, -50%) scale(0.08) rotate(-9deg);
          opacity: 0;
          filter: blur(12px);
        }

        .logo-svg {
          width: 100%;
          height: auto;
          display: block;
          overflow: visible;
        }

        .logo-outline {
          fill: none;
          stroke-width: 2.1;
          stroke-linecap: round;
          stroke-linejoin: round;
        }

        .logo-fill {
          opacity: 0;
        }

        .logo-shine {
          opacity: 0;
        }

        .status {
          position: absolute;
          left: 50%;
          transform: translateX(-50%) translateY(8px);
          display: flex;
          align-items: center;
          gap: 8px;
          top: calc(50% + 102px);
          opacity: 0;
        }

        .status-dot {
          width: 4px;
          height: 4px;
          border-radius: 50%;
          background-color: #69c8d9;
          box-shadow: 0 0 10px rgba(105,200,217,0.8);
        }

        .status-text {
          margin: 0;
          color: rgba(255, 255, 255, 0.5);
          font-size: 9px;
          font-weight: 900;
          letter-spacing: 0.32em;
          text-transform: uppercase;
          white-space: nowrap;
        }

        .progress {
          position: absolute;
          left: 50%;
          transform: translateX(-50%);
          top: calc(50% + 120px);
          width: 68px;
          height: 1px;
          overflow: hidden;
          border-radius: 9999px;
          background-color: rgba(255, 255, 255, 0.1);
          opacity: 0;
        }

        .progress-sweep {
          position: absolute;
          inset: 0;
          transform: translateX(-100%);
          background: linear-gradient(90deg, transparent, #69c8d9, #416eb5);
        }

        /* Ambient background grid */
        .loader-page-grid::before {
          content: "";
          position: absolute;
          inset: 0;
          z-index: -2;
          opacity: .22;
          background-image:
            linear-gradient(rgba(255,255,255,.028) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,255,255,.028) 1px, transparent 1px);
          background-size: 42px 42px;
          mask-image: radial-gradient(circle at center, #000 0%, transparent 68%);
          animation: gridDrift 12s linear infinite;
        }

        .loader-page-grid::after {
          content: "";
          position: absolute;
          inset: -25%;
          z-index: -3;
          opacity: .12;
          background: conic-gradient(from 180deg at 50% 50%, transparent 0 25%, rgba(105,200,217,.22), transparent 38% 68%, rgba(65,110,181,.18), transparent 82% 100%);
          animation: ambientSpin 18s linear infinite;
          filter: blur(70px);
        }

        @keyframes gridDrift { to { background-position: 42px 42px, 42px 42px; } }
        @keyframes ambientSpin { to { transform: rotate(360deg); } }

        /* Animation Cycle Parameters matching your custom 3.2s variables natively */
        .ani-halo { animation: halo 3.2s cubic-bezier(.2,.75,.25,1) infinite; }
        .ani-orbit { animation: orbitAppear 3.2s ease infinite; }
        .ani-drop { animation: dropMorph 3.2s cubic-bezier(.22,.75,.22,1) infinite; transform-origin: center; transform: translateZ(0); will-change: transform, opacity; }
        .ani-trail { animation: trail 3.2s cubic-bezier(0.22, 1, 0.36, 1) infinite; transform-origin: bottom; }
        .ani-ring { animation: impactRing 3.2s ease-out infinite; }
        .ani-particle { animation: particleBurst 3.2s ease-out infinite; transform: translateZ(0); }
        .ani-logo-wrap { animation: logoForm 3.2s cubic-bezier(.17,.84,.24,1) infinite; transform: translateZ(0); will-change: transform, opacity, filter; }
        .ani-stroke { animation: drawStroke 3.2s cubic-bezier(.55,0,.25,1) infinite; stroke-dasharray: 720; stroke-dashoffset: 720; }
        .ani-revealRect { animation: fillReveal 3.2s cubic-bezier(.42,0,.2,1) infinite; }
        .ani-fill { animation: fillOpacity 3.2s ease infinite; }
        .ani-shine { animation: shineOpacity 3.2s ease infinite; }
        .ani-shineBar { animation: shineMove 3.2s cubic-bezier(.5,0,.35,1) infinite; }
        .ani-status { animation: statusIn 3.2s ease infinite; }
        .ani-status-dot { animation: statusPulse 1.05s cubic-bezier(0.22, 1, 0.36, 1) infinite; }
        .ani-progress { animation: progressIn 3.2s ease infinite; }
        .ani-progress-sweep { animation: progressSweep 3.2s cubic-bezier(.2,.7,.2,1) infinite; }

        @keyframes dropMorph {
          0%, 3%   { transform: translateY(-184px) scale(.55,.8); opacity: 0; }
          7%       { opacity: 1; }
          15%      { transform: translateY(-123px) scale(.76,1.22); }
          22%      { transform: translateY(-43px) scale(.88,1.12); }
          27.2%    { transform: translateY(0) scale(1.35,.65); }
          29.4%    { transform: translateY(-9px) scale(.78,1.2); }
          31.6%    { transform: translateY(0) scale(1.08,.88); }
          34%      { transform: translateY(0) scale(1); opacity: 1; border-radius: 50%; }
          39%      { transform: translateY(0) scale(3.8,.22) rotate(0deg); border-radius: 55% 45% 52% 48%; opacity: .95; }
          44%      { transform: translateY(0) scale(8.2,7.2) rotate(16deg); border-radius: 38% 62% 55% 45%; opacity: .18; filter: blur(4px); }
          48%, 100%{ transform: translateY(0) scale(9.2); opacity: 0; filter: blur(8px); }
        }
        @keyframes trail {
          0%, 5% { opacity: 0; transform: translateY(-176px) scaleY(0); }
          11% { opacity: .65; transform: translateY(-176px) scaleY(.6); }
          24% { opacity: .22; transform: translateY(-34px) scaleY(.25); }
          28%, 100% { opacity: 0; transform: translateY(-5px) scaleY(0); }
        }
        @keyframes impactRing {
          0%, 26.5% { opacity: 0; transform: translate(-50%, -50%) scale(.3); }
          28% { opacity: .85; }
          38% { opacity: 0; transform: translate(-50%, -50%) scale(7.5); }
          100% { opacity: 0; }
        }
        @keyframes particleBurst {
          0%, 27% { opacity: 0; transform: translate(0,0) scale(.2); }
          29% { opacity: .9; }
          42% { opacity: 0; transform: translate(var(--x), var(--y)) scale(1); }
          100% { opacity: 0; }
        }
        @keyframes logoForm {
          0%, 35% { opacity: 0; transform: translate(-50%, -50%) scale(.08) rotate(-9deg); filter: blur(8px); }
          41% { opacity: .65; transform: translate(-50%, -50%) scale(.46) rotate(3deg); filter: blur(3px); }
          48% { opacity: 1; transform: translate(-50%, -50%) scale(1.045) rotate(-.8deg); filter: blur(0); }
          53% { transform: translate(-50%, -50%) scale(.985) rotate(.25deg); }
          58%, 84% { opacity: 1; transform: translate(-50%, -50%) scale(1) rotate(0); filter: blur(0); }
          91%, 100% { opacity: 0; transform: translate(-50%, -50%) scale(.94); filter: blur(3px); }
        }
        @keyframes drawStroke {
          0%, 37% { opacity: 0; stroke-dashoffset: 720; }
          41% { opacity: 1; }
          62% { opacity: .72; stroke-dashoffset: 0; }
          72% { opacity: .1; stroke-dashoffset: 0; }
          78%, 100% { opacity: 0; stroke-dashoffset: 0; }
        }
        @keyframes fillReveal {
          0%, 53% { y: 154px; height: 0; }
          69%, 100% { y: -2px; height: 156px; }
        }
        @keyframes fillOpacity {
          0%, 52% { opacity: 0; }
          56% { opacity: .2; }
          68%, 88% { opacity: 1; }
          93%, 100% { opacity: 0; }
        }
        @keyframes shineOpacity {
          0%, 66% { opacity: 0; }
          70% { opacity: .75; }
          78% { opacity: .18; }
          82%, 100% { opacity: 0; }
        }
        @keyframes shineMove {
          0%, 66% { transform: translateX(-90px) rotate(18deg); }
          81%, 100% { transform: translateX(245px) rotate(18deg); }
        }
        @keyframes halo {
          0%, 33% { opacity: 0; transform: translate(-50%, -50%) scale(.2); }
          48% { opacity: .9; transform: translate(-50%, -50%) scale(.75); }
          69% { opacity: .55; transform: translate(-50%, -50%) scale(1.12); }
          87% { opacity: .2; transform: translate(-50%, -50%) scale(1.2); }
          94%, 100% { opacity: 0; transform: translate(-50%, -50%) scale(1.35); }
        }
        @keyframes orbitAppear {
          0%, 45% { opacity: 0; transform: translate(-50%, -50%) scale(.55) rotate(-18deg); }
          57% { opacity: .75; }
          84% { opacity: .25; transform: translate(-50%, -50%) scale(1) rotate(128deg); }
          93%, 100% { opacity: 0; transform: translate(-50%, -50%) scale(1.05) rotate(185deg); }
        }
        @keyframes statusIn {
          0%, 56% { opacity: 0; transform: translateX(-50%) translateY(8px); }
          66%, 86% { opacity: 1; transform: translateX(-50%) translateY(0); }
          94%, 100% { opacity: 0; transform: translateX(-50%) translateY(-3px); }
        }
        @keyframes progressIn { 0%, 58% { opacity: 0; } 65%, 87% { opacity: 1; } 95%, 100% { opacity: 0; } }
        @keyframes progressSweep { 0%, 61% { transform: translateX(-100%); } 85%, 100% { transform: translateX(100%); } }
        @keyframes statusPulse { 0%, 100% { transform: scale(.75); opacity: .45; } 50% { transform: scale(1.3); opacity: 1; } }
      `}</style>

            {/* Grid Layout Core Background Panel */}
            <div className="loader-page-grid">

                {/* Core Component Box */}
                <div className="loader-core-box">
                    <div className="halo ani-halo" style={{ background: 'radial-gradient(circle, rgba(105,200,217,.12), rgba(65,110,181,.035) 47%, transparent 70%)' }}></div>

                    <div
                        className="orbit ani-orbit"
                        style={{ borderLeftColor: 'transparent', borderBottomColor: 'rgba(65,110,181,.08)' }}
                    />

                    <div className="trail ani-trail" style={{ background: 'linear-gradient(to top, rgba(105,200,217,.55), transparent)' }} />
                    <div className="impact-ring ani-ring" />

                    {/* Burst Particle Array */}
                    <span className="particle ani-particle" style={{ '--x': '-54px', '--y': '-20px', animationDelay: '0s' }} />
                    <span className="particle ani-particle" style={{ '--x': '45px', '--y': '-35px', animationDelay: '.025s' }} />
                    <span className="particle ani-particle" style={{ '--x': '-37px', '--y': '42px', animationDelay: '.045s' }} />
                    <span className="particle ani-particle" style={{ '--x': '58px', '--y': '27px', animationDelay: '.065s' }} />
                    <span className="particle ani-particle" style={{ '--x': '5px', '--y': '-63px', animationDelay: '.02s' }} />
                    <span className="particle ani-particle" style={{ '--x': '12px', '--y': '59px', animationDelay: '.055s' }} />

                    {/* Morphing Liquid Droplet Layer */}
                    <div
                        className="drop ani-drop"
                        style={{
                            background: 'linear-gradient(145deg, #8ce7f1 0%, #69c8d9 32%, #416eb5 100%)',
                        }}
                    />

                    {/* Master Vector Container Box */}
                    <div className="logo-wrap ani-logo-wrap">
                        <svg className="logo-svg" viewBox="0 0 179 151.79" xmlns="http://www.w3.org/2000/svg" role="img" aria-label={`${brandName} Identity Graphic`}>
                            <defs>
                                <linearGradient id="logo-gradient" x1="0" y1="75.9" x2="145.5" y2="75.9" gradientUnits="userSpaceOnUse">
                                    <stop offset="0" stopColor="#69c8d9" />
                                    <stop offset="1" stopColor="#416eb5" />
                                </linearGradient>
                                <linearGradient id="logo-gradient-2" x1="138.62" y1="140.98" x2="179" y2="140.98" gradientUnits="userSpaceOnUse">
                                    <stop offset="0" stopColor="#69c8d9" />
                                    <stop offset="1" stopColor="#416eb5" />
                                </linearGradient>
                                <clipPath id="fill-reveal" clipPathUnits="userSpaceOnUse">
                                    <rect id="revealRect" className="ani-revealRect" x="-4" y="154" width="188" height="0" />
                                </clipPath>
                                <clipPath id="logo-clip" clipPathUnits="userSpaceOnUse">
                                    <path d="m76.18,4.75L1.24,136.84c-3.55,6.25.97,14.01,8.16,14.01h15.77c3.39,0,6.51-1.82,8.17-4.77l45.79-81.24c3.56-6.31,12.61-6.38,16.27-.13l11.91,20.38c1.72,2.94,1.71,6.57-.02,9.5l-14.58,24.69c-1.69,2.86-4.76,4.61-8.08,4.61h-14.24c-7.19,0-11.71,7.76-8.16,14.01l5.19,9.14c1.67,2.93,4.78,4.75,8.16,4.75h30.03c3.35,0,6.44-1.79,8.12-4.69l30.51-52.78c1.68-2.91,1.68-6.51-.02-9.42L97.43,4.65c-1.68-2.88-4.77-4.65-8.1-4.65h-4.99c-3.38,0-6.49,1.81-8.16,4.75Z" />
                                    <path d="m139.16,138.17l5.29,9.84c.78,1.45,2.29,2.35,3.93,2.35h26.16c3.44,0,5.58-3.72,3.86-6.7l-5.7-9.84c-.8-1.38-2.27-2.22-3.86-2.22h-25.75c-3.37,0-5.53,3.6-3.93,6.57Z" />
                                </clipPath>
                                <linearGradient id="shine-gradient" x1="0" y1="0" x2="1" y2="0">
                                    <stop offset="0" stopColor="#fff" stopOpacity="0" />
                                    <stop offset=".5" stopColor="#fff" stopOpacity=".9" />
                                    <stop offset="1" stopColor="#fff" stopOpacity="0" />
                                </linearGradient>
                            </defs>

                            {/* Outline Path Vector Mesh */}
                            <g className="logo-outline" style={{ fill: 'none', stroke: 'url(#logo-gradient)', strokeWidth: 2.1, strokeLinecap: 'round', strokeLinejoin: 'round' }}>
                                <path className="ani-stroke" d="m76.18,4.75L1.24,136.84c-3.55,6.25.97,14.01,8.16,14.01h15.77c3.39,0,6.51-1.82,8.17-4.77l45.79-81.24c3.56-6.31,12.61-6.38,16.27-.13l11.91,20.38c1.72,2.94,1.71,6.57-.02,9.5l-14.58,24.69c-1.69,2.86-4.76,4.61-8.08,4.61h-14.24c-7.19,0-11.71,7.76-8.16,14.01l5.19,9.14c1.67,2.93,4.78,4.75,8.16,4.75h30.03c3.35,0,6.44-1.79,8.12-4.69l30.51-52.78c1.68-2.91,1.68-6.51-.02-9.42L97.43,4.65c-1.68-2.88-4.77-4.65-8.1-4.65h-4.99c-3.38,0-6.49,1.81-8.16,4.75Z" style={{ vectorEffect: 'non-scaling-stroke' }} />
                                <path className="ani-stroke" d="m139.16,138.17l5.29,9.84c.78,1.45,2.29,2.35,3.93,2.35h26.16c3.44,0,5.58-3.72,3.86-6.7l-5.7-9.84c-.8-1.38-2.27-2.22-3.86-2.22h-25.75c-3.37,0-5.53,3.6-3.93,6.57Z" style={{ vectorEffect: 'non-scaling-stroke' }} />
                            </g>

                            {/* Core Gradient Fill Block */}
                            <g className="logo-fill ani-fill" style={{ clipPath: 'url(#fill-reveal)' }}>
                                <path fill="url(#logo-gradient)" d="m76.18,4.75L1.24,136.84c-3.55,6.25.97,14.01,8.16,14.01h15.77c3.39,0,6.51-1.82,8.17-4.77l45.79-81.24c3.56-6.31,12.61-6.38,16.27-.13l11.91,20.38c1.72,2.94,1.71,6.57-.02,9.5l-14.58,24.69c-1.69,2.86-4.76,4.61-8.08,4.61h-14.24c-7.19,0-11.71,7.76-8.16,14.01l5.19,9.14c1.67,2.93,4.78,4.75,8.16,4.75h30.03c3.35,0,6.44-1.79,8.12-4.69l30.51-52.78c1.68-2.91,1.68-6.51-.02-9.42L97.43,4.65c-1.68-2.88-4.77-4.65-8.1-4.65h-4.99c-3.38,0-6.49,1.81-8.16,4.75Z" />
                                <path fill="url(#logo-gradient-2)" d="m139.16,138.17l5.29,9.84c.78,1.45,2.29,2.35,3.93,2.35h26.16c3.44,0,5.58-3.72,3.86-6.7l-5.7-9.84c-.8-1.38-2.27-2.22-3.86-2.22h-25.75c-3.37,0-5.53,3.6-3.93,6.57Z" />
                            </g>

                            {/* Dynamic Chrome Shimmer Overlay Beam */}
                            <g className="logo-shine ani-shine" style={{ clipPath: 'url(#logo-clip)' }}>
                                <rect id="shineBar" className="ani-shineBar" x="-70" y="-45" width="24" height="255" fill="url(#shine-gradient)" transform="rotate(18)" />
                            </g>
                        </svg>
                    </div>

                    {/* Dynamic Typography Branding Node */}
                    <div className="status ani-status">
                        <span className="status-dot ani-status-dot" />
                        <p className="status-text">
                            {brandName}
                        </p>
                    </div>

                    {/* Radial Horizontal Progress Track Rule */}
                    <div className="progress ani-progress">
                        <div className="progress-sweep ani-progress-sweep" />
                    </div>

                </div>
            </div>
        </main>
    );
}